import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const authApi = axios.create({
  baseURL: API,
  withCredentials: true
});

// Add token to requests if available
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authApi.get('/auth/me');
      setUserState(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      setUserState(null);
      setIsAuthenticated(false);
      localStorage.removeItem('auth_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await authApi.post('/auth/login', { email, password });
    // Store token in localStorage as backup
    if (response.data.access_token) {
      localStorage.setItem('auth_token', response.data.access_token);
    }
    setUserState(response.data.user);
    setIsAuthenticated(true);
    return response.data;
  };

  const register = async (name, email, password, role = 'student') => {
    const response = await authApi.post('/auth/register', { name, email, password, role });
    // Store token in localStorage as backup
    if (response.data.access_token) {
      localStorage.setItem('auth_token', response.data.access_token);
    }
    setUserState(response.data.user);
    setIsAuthenticated(true);
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processOAuthSession = async (sessionId) => {
    const response = await authApi.post('/auth/session', { session_id: sessionId });
    // Store token in localStorage as backup
    if (response.data.access_token) {
      localStorage.setItem('auth_token', response.data.access_token);
    }
    setUserState(response.data.user);
    setIsAuthenticated(true);
    return response.data;
  };

  const logout = async () => {
    try {
      await authApi.post('/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('auth_token');
    setUserState(null);
    setIsAuthenticated(false);
  };

  const setUser = (userData) => {
    setUserState(userData);
    setIsAuthenticated(!!userData);
  };

  const updateUser = (userData) => {
    setUserState(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      login,
      register,
      loginWithGoogle,
      processOAuthSession,
      logout,
      updateUser,
      setUser,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
